import React, { useState } from "react";
import { Link, AlertCircle, CheckCircle, Loader, Lock } from "lucide-react";

// Secure connection config - no API tokens on client side
interface SecureJiraConfig {
  baseUrl: string;
  email: string;
  projectKey: string;
}

interface JiraConnectionProps {
  onConnect: (config: SecureJiraConfig) => Promise<void>;
  onDisconnect: () => void;
  connected: boolean;
  loading: boolean;
  error: string | null;
}

export const JiraConnection: React.FC<JiraConnectionProps> = ({
  onConnect,
  onDisconnect,
  connected,
  loading,
  error,
}) => {
  const [config, setConfig] = useState<SecureJiraConfig>({
    baseUrl: "",
    email: "",
    projectKey: "",
  });

  const [showForm, setShowForm] = useState(!connected);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onConnect(config);
  };

  const handleDisconnect = () => {
    onDisconnect();
    setShowForm(true);
  };

  if (connected && !showForm) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Connected to Jira
              </h3>
              <p className="text-sm text-gray-600">
                Project: {config.projectKey} • {config.baseUrl}
              </p>
            </div>
          </div>
          <button
            onClick={handleDisconnect}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Disconnect
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-blue-50 rounded-lg">
          <Link className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Connect to Jira
          </h3>
          <p className="text-sm text-gray-600">
            Enter your Jira credentials to sync project data
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Jira Base URL
          </label>
          <input
            type="url"
            value={config.baseUrl}
            onChange={(e) =>
              setConfig((prev) => ({ ...prev, baseUrl: e.target.value }))
            }
            placeholder="https://your-domain.atlassian.net"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Your Jira Cloud instance URL
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={config.email}
            onChange={(e) =>
              setConfig((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder="your-email@company.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-amber-900 mb-1">
                Secure Authentication
              </h4>
              <p className="text-xs text-amber-800">
                Your API token will be requested securely after you provide your
                Jira details. Tokens are never stored in your browser and are
                encrypted on our servers.
              </p>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project Key
          </label>
          <input
            type="text"
            value={config.projectKey}
            onChange={(e) =>
              setConfig((prev) => ({
                ...prev,
                projectKey: e.target.value.toUpperCase(),
              }))
            }
            placeholder="PROJ"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            The key of your Jira project (e.g., PROJ, DEV, etc.)
          </p>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Connecting...</span>
            </>
          ) : (
            <>
              <Link className="w-4 h-4" />
              <span>Connect to Jira</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">
          Setup Instructions:
        </h4>
        <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
          <li>Enter your Jira Cloud instance URL</li>
          <li>Enter your email address associated with Jira</li>
          <li>Enter your Jira project key</li>
          <li>Click "Connect to Jira" to continue</li>
          <li>You'll be securely prompted for your API token next</li>
        </ol>

        <div className="mt-3 p-3 bg-white rounded border border-blue-200">
          <p className="text-xs text-blue-800">
            <strong>🔐 Security Note:</strong> Your API token is never stored in
            your browser. All credentials are encrypted and stored securely on
            our servers.
          </p>
        </div>
      </div>
    </div>
  );
};
